/**
 * Performance optimization utilities for the standardized page layout system
 */

/**
 * Preload critical resources for better performance
 * @param {Array<string>} resources - Array of resource URLs to preload
 * @param {string} [type='image'] - Resource type (image, script, style, font)
 */
export const preloadResources = (resources, type = 'image') => {
  if (!Array.isArray(resources)) return;

  resources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource;
    
    switch (type) {
      case 'image':
        link.as = 'image';
        break;
      case 'script':
        link.as = 'script';
        break;
      case 'style':
        link.as = 'style';
        break;
      case 'font':
        link.as = 'font';
        link.crossOrigin = 'anonymous';
        break;
      default:
        link.as = 'fetch';
        link.crossOrigin = 'anonymous';
    }
    
    document.head.appendChild(link);
  });
};

/**
 * Optimize images by setting appropriate loading attributes
 * @param {HTMLElement} container - Container element to optimize images within
 */
export const optimizeImages = (container = document) => {
  const images = container.querySelectorAll('img:not([loading])');
  
  images.forEach((img, index) => {
    // First few images should load immediately (above the fold)
    if (index < 3) {
      img.loading = 'eager';
      img.fetchPriority = 'high';
    } else {
      img.loading = 'lazy';
      img.decoding = 'async';
    }
  });
};

/**
 * Remove unused CSS classes from elements to reduce DOM complexity
 * @param {HTMLElement} container - Container to clean up
 * @param {Array<string>} unusedClasses - Array of unused class names to remove
 */
export const removeUnusedClasses = (container = document.body, unusedClasses = []) => {
  // Common unused classes from legacy implementations
  const defaultUnusedClasses = [
    'wine-section',
    'jeep-section', 
    'futbol-section',
    'pane-section',
    'rossetti-section',
    'tomato-section',
    'wedding-section',
    'rawrock-section',
    'plymouth-section',
    'southington-section',
    'travel-section',
    'thanksgiving-section',
    'hello-section',
    'bread-section'
  ];
  
  const classesToRemove = [...defaultUnusedClasses, ...unusedClasses];
  
  classesToRemove.forEach(className => {
    const elements = container.querySelectorAll(`.${className}`);
    elements.forEach(element => {
      element.classList.remove(className);
    });
  });
};

/**
 * Optimize CSS by removing unused styles and consolidating rules
 * @param {Array<string>} unusedSelectors - Array of unused CSS selectors
 */
export const optimizeCSS = (unusedSelectors = []) => {
  const styleSheets = Array.from(document.styleSheets);
  
  styleSheets.forEach(sheet => {
    try {
      const rules = Array.from(sheet.cssRules || sheet.rules || []);
      
      rules.forEach((rule, index) => {
        if (rule.type === CSSRule.STYLE_RULE) {
          const selector = rule.selectorText;
          
          // Remove unused selectors
          if (unusedSelectors.some(unused => selector.includes(unused))) {
            sheet.deleteRule(index);
          }
        }
      });
    } catch (e) {
      // Cross-origin stylesheets can't be accessed
      console.warn('Cannot optimize cross-origin stylesheet:', sheet.href);
    }
  });
};

/**
 * Measure and log performance metrics
 * @param {string} [label='Performance'] - Label for the measurement
 * @returns {Object} Performance metrics
 */
export const measurePerformance = (label = 'Performance') => {
  const metrics = {};
  
  // Core Web Vitals
  if ('web-vital' in window) {
    // This would integrate with web-vitals library if available
    metrics.webVitals = 'Available';
  }
  
  // Navigation timing
  if (performance.navigation) {
    metrics.navigation = {
      type: performance.navigation.type,
      redirectCount: performance.navigation.redirectCount
    };
  }
  
  // Resource timing
  const resources = performance.getEntriesByType('resource');
  metrics.resources = {
    total: resources.length,
    images: resources.filter(r => r.initiatorType === 'img').length,
    scripts: resources.filter(r => r.initiatorType === 'script').length,
    stylesheets: resources.filter(r => r.initiatorType === 'link').length
  };
  
  // Memory usage (if available)
  if (performance.memory) {
    metrics.memory = {
      used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
      total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
      limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)
    };
  }
  
  // Paint timing
  const paintEntries = performance.getEntriesByType('paint');
  metrics.paint = {};
  paintEntries.forEach(entry => {
    metrics.paint[entry.name] = Math.round(entry.startTime);
  });
  
  console.group(`${label} Metrics`);
  console.table(metrics);
  console.groupEnd();
  
  return metrics;
};

/**
 * Debounce function for performance optimization
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @param {boolean} immediate - Execute immediately on first call
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait, immediate = false) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

/**
 * Throttle function for performance optimization
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Check if user prefers reduced motion
 * @returns {boolean} True if user prefers reduced motion
 */
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Optimize animations based on user preferences and device capabilities
 * @param {HTMLElement} element - Element to optimize animations for
 */
export const optimizeAnimations = (element) => {
  if (prefersReducedMotion()) {
    element.style.animation = 'none';
    element.style.transition = 'none';
  }
  
  // Reduce animations on low-end devices
  if (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) {
    element.style.animationDuration = '0.1s';
    element.style.transitionDuration = '0.1s';
  }
};

/**
 * Initialize performance optimizations for the page
 * @param {Object} options - Optimization options
 */
export const initializePerformanceOptimizations = (options = {}) => {
  const {
    preloadImages = [],
    removeUnused = true,
    measureMetrics = true,
    optimizeImagesOnLoad = true,
    disableForGalleries = false // New option to disable aggressive optimizations for gallery pages
  } = options;
  
  // Preload critical images
  if (preloadImages.length > 0) {
    preloadResources(preloadImages, 'image');
  }
  
  // Remove unused classes (but be more conservative on gallery pages)
  if (removeUnused && !disableForGalleries) {
    removeUnusedClasses();
  }
  
  // Optimize images when DOM is ready (but be gentler on gallery pages)
  if (optimizeImagesOnLoad) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        // Don't optimize gallery images aggressively
        const container = disableForGalleries ? 
          document.querySelector('.page-content:not(.gallery-page)') || document : 
          document;
        optimizeImages(container);
      });
    } else {
      const container = disableForGalleries ? 
        document.querySelector('.page-content:not(.gallery-page)') || document : 
        document;
      optimizeImages(container);
    }
  }
  
  // Measure performance metrics
  if (measureMetrics) {
    window.addEventListener('load', () => {
      setTimeout(() => measurePerformance('Page Load'), 1000);
    });
  }
};

export default {
  preloadResources,
  optimizeImages,
  removeUnusedClasses,
  optimizeCSS,
  measurePerformance,
  debounce,
  throttle,
  prefersReducedMotion,
  optimizeAnimations,
  initializePerformanceOptimizations
};